import { HttpInterceptorFn, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, switchMap, BehaviorSubject, filter, take, from } from 'rxjs';
import { AuthService } from '../../application/services/auth.service';
import { environment } from '../../../environments/environment';

// Cola de requests que llegaron con 401 mientras se refrescaba el token
let isRefreshing    = false;
let isHandling403   = false;
const refreshDone$ = new BehaviorSubject<boolean>(false);

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const http        = inject(HttpClient);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isAuthUrl = req.url.includes('/Auth/SignOut') || req.url.includes('/Auth/Refresh')
                     || req.url.includes('/Auth/SignIn') || req.url.includes('/Csrf/');

      // 403 → renovar CSRF y reintentar una sola vez; si vuelve a fallar, cerrar sesión.
      // isHandling403 evita que requests concurrentes disparen múltiples ciclos.
      if (error.status === 403) {
        if (isAuthUrl || isHandling403) { authService.logout(); return throwError(() => error); }
        isHandling403 = true;
        return from(authService.fetchCsrfToken()).pipe(
          switchMap(() => next(req).pipe(
            catchError((retryError: HttpErrorResponse) => {
              isHandling403 = false;
              authService.logout();
              return throwError(() => retryError);
            }),
          )),
          catchError((e: HttpErrorResponse) => {
            isHandling403 = false;
            authService.logout();
            return throwError(() => e);
          }),
        );
      }

      // Solo manejamos 401
      if (error.status !== 401) return throwError(() => error);

      // No reintentar refresh ni signin para evitar loops
      if (isAuthUrl) {
        authService.logout();
        return throwError(() => error);
      }

      // Si ya hay un refresh en curso, encolar y esperar
      if (isRefreshing) {
        return refreshDone$.pipe(
          filter(done => done),
          take(1),
          switchMap(() => next(req)),
        );
      }

      isRefreshing = true;
      refreshDone$.next(false);

      return http.post(
        `${environment.apiUrl}/public/v1/Auth/Refresh`,
        { Device: authService.getDevice(), Platform: 'CRM' },
        { withCredentials: true },
      ).pipe(
        switchMap(() => {
          isRefreshing = false;
          refreshDone$.next(true);
          authService.fetchCsrfToken(); // renovar CSRF en background
          // El reintento tiene su propio catchError: solo cierra sesión si vuelve a dar 401
          return next(req).pipe(
            catchError((retryError: HttpErrorResponse) => {
              if (retryError.status === 401) authService.logout();
              return throwError(() => retryError);
            }),
          );
        }),
        catchError((refreshError: HttpErrorResponse) => {
          isRefreshing = false;
          refreshDone$.next(false);
          // Solo logout si el refresh en sí falló con 401; otros errores los propaga
          if (refreshError.status === 401) authService.logout();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
